import type { ReactNode } from "react";
import { StrictMode } from "react";
import { createRoot, type Root } from "react-dom/client";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "clearauth/react";
import { App } from "../App.tsx";
import { renderBootstrapRecovery } from "./bootstrap-recovery.ts";

type RootFactory = (container: HTMLElement) => Pick<Root, "render">;

export function bootstrapApplication({
  root,
  apiUrl,
  queryClient,
  releaseId,
  app,
  rootFactory = createRoot,
}: {
  root: HTMLElement;
  apiUrl: string;
  queryClient: QueryClient;
  releaseId?: string;
  app?: ReactNode;
  rootFactory?: RootFactory;
}): void {
  try {
    rootFactory(root).render(
      app ?? (
        <StrictMode>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <AuthProvider baseUrl={`${apiUrl}/api/auth`}>
                <App />
              </AuthProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </StrictMode>
      ),
    );
    (window as Window & { __HC_APP_BOOTED__?: boolean }).__HC_APP_BOOTED__ = true;
  } catch (error) {
    const developerMessage = import.meta.env.DEV
      ? (error instanceof Error ? error.stack || error.message : String(error))
      : undefined;
    renderBootstrapRecovery(root, releaseId, developerMessage);
    console.error("[main] bootstrap failed:", error);
  }
}
